import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: var(--surface-color);
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 115, 234, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  ${props => props.$variant === 'primary' ? `
    background-color: var(--primary-color);
    color: white;
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }
  ` : `
    background-color: transparent;
    color: var(--text-secondary);
    border: 1px solid var(--border-color);
    
    &:hover:not(:disabled) {
      background-color: var(--hover-background);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const UserInfo = styled.div`
  background-color: var(--background-color);
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const UserName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const UserEmail = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

interface ResetPasswordFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newPassword: string) => Promise<void>;
  userFullName?: string;
  userEmail?: string;
  isLoading?: boolean;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userFullName,
  userEmail,
  isLoading = false
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!password || password.length < 6) {
      newErrors.password = 'סיסמה חייבת להכיל לפחות 6 תווים';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'הסיסמאות אינן זהות';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(password);
      setPassword('');
      setConfirmPassword('');
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (errors.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  if (!isOpen) return null;

  return (
    <Overlay onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <Modal>
        <ModalHeader>
          <ModalTitle>איפוס סיסמה</ModalTitle>
          <CloseButton onClick={onClose} type="button">×</CloseButton>
        </ModalHeader>
        
        {userFullName && (
          <UserInfo>
            <UserName>{userFullName}</UserName>
            {userEmail && <UserEmail>{userEmail}</UserEmail>}
          </UserInfo>
        )}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>סיסמה חדשה *</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              className={errors.password ? 'error' : ''}
              disabled={isLoading}
              placeholder="הזן סיסמה חדשה (לפחות 6 תווים)"
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>אימות סיסמה *</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              className={errors.confirmPassword ? 'error' : ''}
              disabled={isLoading}
              placeholder="הזן שוב את הסיסמה החדשה"
            />
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose} disabled={isLoading}>
              ביטול
            </Button>
            <Button type="submit" $variant="primary" disabled={isLoading}>
              {isLoading ? 'מעדכן...' : 'עדכן סיסמה'}
            </Button>
          </ButtonGroup>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default ResetPasswordForm;